import React from 'react';
import type { TicketPipelineResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';

export interface PipelinesSidebarProps {
  pipelines: TicketPipelineResponse[];
  selectedPipelineId: string | null;
  onPipelineChange: (pipelineId: string | null) => void;
  onSettings?: () => void;
  loading?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  metrics?: {
    total: number;
    open: number;
    slaBreached: number;
  };
}

/**
 * Rail de Pipelines — anatomia `.pipes` EXATA do mockup helpdesk-tickets:
 * 250px (60px recolhida), header "Pipelines" + colapso, linhas `.pipe-row`
 * (dot 9px quadrado-arredondado + nome + count, ativa = lilás), rodapé
 * `.pipes-foot` com métricas (Total/Abertos/SLA violado) + "Gerenciar Pipelines".
 * Escondida no mobile (o seletor de pipeline vive nos chips do m-tools).
 */
export const PipelinesSidebar: React.FC<PipelinesSidebarProps> = ({
  pipelines,
  selectedPipelineId,
  onPipelineChange,
  onSettings,
  loading = false,
  isOpen = false,
  onToggle,
  metrics
}) => {
  const collapsed = !isOpen;

  return (
    <aside
      className="hidden sm:flex h-full flex-shrink-0 flex-col min-h-0 transition-all duration-200"
      style={{
        width: collapsed ? 60 : 250,
        backgroundColor: 'var(--theme-sidebar-background)',
        borderRight: '1px solid var(--theme-sidebar-border)',
      }}
      data-testid="pipelines-sidebar"
    >
      {/* `.pipes-h`: título + botão de colapso 32px */}
      <div
        className={`flex-none flex items-center gap-2 ${collapsed ? 'justify-center' : ''}`}
        style={{ padding: collapsed ? '13px 0 9px' : '13px 12px 9px' }}
      >
        {!collapsed && (
          <span className="flex-1 font-bold text-[13px] tracking-tight" style={{ color: 'var(--theme-sidebar-text)' }}>
            Pipelines
          </span>
        )}
        {onToggle && (
          <button
            onClick={onToggle}
            className="w-8 h-8 rounded-[7px] flex items-center justify-center transition-colors flex-none"
            style={{ color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            title={collapsed ? 'Expandir pipelines' : 'Recolher pipelines'}
            data-testid="pipelines-toggle"
          >
            <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* `.pipes-list`: linhas 38px, dot 9px, nome + count */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-[2px]" style={{ padding: '2px 8px 8px' }}>
        {loading && (
          <>
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-[38px] rounded-lg animate-pulse" style={{ backgroundColor: 'var(--theme-border)' }} />
            ))}
          </>
        )}
        {!loading && pipelines.map((pipeline) => {
          const on = selectedPipelineId === pipeline.id;
          return (
            <button
              key={pipeline.id}
              onClick={() => onPipelineChange(pipeline.id)}
              className={`flex items-center w-full rounded-lg text-left transition-colors ${collapsed ? 'justify-center' : 'gap-2.5'}`}
              style={{
                height: 38,
                padding: collapsed ? 0 : '0 10px',
                backgroundColor: on ? 'var(--theme-primary-background)' : 'transparent',
                color: on ? 'var(--theme-primary)' : 'var(--theme-sidebar-text)',
              }}
              onMouseEnter={(e) => { if (!on) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
              onMouseLeave={(e) => { if (!on) e.currentTarget.style.backgroundColor = 'transparent'; }}
              title={pipeline.name}
              data-testid={`pipeline-row-${pipeline.id}`}
            >
              <span
                className="flex-none"
                style={{ width: 9, height: 9, borderRadius: 3, backgroundColor: pipeline.color || '#64748b' }}
              />
              {!collapsed && (
                <span className={`flex-1 min-w-0 truncate text-[13px] ${on ? 'font-semibold' : 'font-medium'}`}>
                  {pipeline.name}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* `.pipes-foot`: métricas + Gerenciar Pipelines */}
      {!collapsed && (
        <div className="flex-none" style={{ borderTop: '1px solid var(--theme-sidebar-border)', padding: '11px 12px' }}>
          {metrics && (
            <div className="flex flex-col gap-1.5 mb-2.5">
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: 'var(--theme-muted-text)' }}>Total</span>
                <span className="font-bold" style={{ color: 'var(--theme-sidebar-text)', fontVariantNumeric: 'tabular-nums' }}>
                  {metrics.total}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: 'var(--theme-muted-text)' }}>Abertos</span>
                <span className="font-bold" style={{ color: 'var(--theme-sidebar-text)', fontVariantNumeric: 'tabular-nums' }}>
                  {metrics.open}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: 'var(--theme-muted-text)' }}>SLA violado</span>
                <span className="font-bold" style={{ color: '#EF4444', fontVariantNumeric: 'tabular-nums' }}>
                  {metrics.slaBreached}
                </span>
              </div>
            </div>
          )}
          {onSettings && (
            /* `.mini-btn.wide` do mockup */
            <button
              onClick={onSettings}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-colors"
              style={{
                height: 32,
                border: '1px solid var(--theme-input-border)',
                color: 'var(--theme-sidebar-text)',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              data-testid="pipelines-manage"
            >
              <Icon name="settings" className="w-3.5 h-3.5" />
              Gerenciar Pipelines
            </button>
          )}
        </div>
      )}
    </aside>
  );
};
